import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEnabledModules } from '../helpers/extra-modules';
import { getAccessToken } from '../helpers/user';
import http from '../store/http/http-common';
import { APP_NETWORK } from '../network';
import { runSeparationTests, SeparationContext, TestResult } from '../helpers/separation-test';

export default function SeparationTestPage() {
  const [context, setContext] = useState<SeparationContext>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [foreignPlan, setForeignPlan] = useState('');
  const [results, setResults] = useState<TestResult[]>([]);
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        if (!getAccessToken()) throw new Error('Log ind i brugerappen for at teste.');
        const modules = await getEnabledModules();
        if (!Array.isArray(modules) || !modules.some(m => m.key === 'separation-test'))
          throw new Error('Testmodulet er ikke aktiveret for appens netværk. Kontakt netværkets administrator.');
        const response = await http.get('/application/separation-test/context');
        if (response.status !== 200) throw new Error('Dit login har ikke adgang til appens testmodul.');
        if (active) setContext(response.data);
      } catch (e) { if (active) setError(e instanceof Error ? e.message : 'Testmodulet kunne ikke indlæses.'); }
    })();
    return () => { active = false; };
  }, []);
  const run = async () => {
    if (!context) return;
    setBusy(true); setResults([]);
    try {
      setResults(await runSeparationTests('application', context, async (path, method, network) => {
        const token = getAccessToken();
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
            method, headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), 'X-Business-Network': network || APP_NETWORK },
            signal: controller.signal,
          });
          return response.status;
        } finally { clearTimeout(timeout); }
      }, foreignPlan));
    } finally { setBusy(false); }
  };
  return <main className="relative overflow-auto bg-white rounded m-4 p-6 max-w-4xl" aria-label="Test af separation">
    <h1 className="text-2xl font-bold mb-3">Test af separation – brugerapp</h1>
    <p className="mb-3">Kontrollér, at dit login kun har adgang til din virksomhed og de rette funktioner. Testen ændrer ingen virksomhedsdata.</p>
    <Link to="/" className="underline">Tilbage til forsiden</Link>
    {error && <p role="alert" className="my-4">{error}</p>}
    {context && <>
      <p className="my-4">Netværk: {context.network} · Rolle: {context.role}</p>
      <label className="block" htmlFor="foreign-plan">Plan-id fra en anden virksomhed (valgfrit)</label>
      <input id="foreign-plan" className="border rounded p-2 w-full my-2" value={foreignPlan} disabled={busy} onChange={e => { setForeignPlan(e.target.value); setResults([]); }} />
      <p>Brug et eksisterende id fra en anden testbruger. Et ukendt id beviser ikke separation mellem virksomheder.</p>
      <button className="bg-blue-700 text-white rounded px-4 py-2 my-4 disabled:opacity-50" disabled={busy} onClick={run}>{busy ? 'Tester…' : 'Kør test'}</button>
      <p>Grøn = forventet adgang eller afvisning. Rød = uventet svar. Sprunget over = endnu ikke kontrolleret. Kør igen efter ændringer af adgang.</p>
      <div aria-live="polite">{results.map(r => <div key={r.name} className={`border-l-4 p-3 my-3 ${r.status === 'passed' ? 'border-green-600 bg-green-50' : r.status === 'failed' ? 'border-red-600 bg-red-50' : 'border-gray-500 bg-gray-50'}`}>
        <strong>{r.status === 'passed' ? 'Bestået' : r.status === 'failed' ? 'Fejlet' : 'Sprunget over'}: {r.name}</strong><p>{r.detail}</p>
      </div>)}</div>
    </>}
  </main>;
}
